import { BridgeStatus, ConvergenceMatrix, RiskState } from "../../api";

interface Props {
  bridge: BridgeStatus | null;
  risk: RiskState | null;
  matrix: ConvergenceMatrix | null;
  positionsCount: number;
}

type PillTone = "success" | "danger" | "warn" | "neutral";

function Pill({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: PillTone;
}) {
  const dotClass =
    tone === "success"
      ? "bg-success"
      : tone === "danger"
        ? "bg-danger"
        : tone === "warn"
          ? "bg-warn"
          : "bg-fg-muted";
  const textClass =
    tone === "success"
      ? "text-success"
      : tone === "danger"
        ? "text-danger"
        : tone === "warn"
          ? "text-warn"
          : "text-fg-muted";
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3">
      <span
        className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotClass}`}
        aria-hidden="true"
      />
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-fg-muted">
          {label}
        </p>
        <p className={`mt-0.5 font-mono text-sm font-semibold ${textClass}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

export function StatusStrip({ bridge, risk, matrix, positionsCount }: Props) {
  const bridgeConnected = !!bridge?.connected;
  const killOn = risk?.kill_switch.active === true;
  const maxPositions = risk?.limits.max_open_positions_total ?? 0;

  // Convergence: how many symbols currently have at least one
  // agreed/converged strategy. For single-fire mode there's no real
  // notion of convergence; we just count symbols with any active row.
  const convergedCount =
    matrix?.symbols.filter((s) =>
      s.strategies.some((c) => c.action != null),
    ).length ?? 0;
  const symbolCount = matrix?.symbols.length ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Pill
        label="Bridge"
        value={bridgeConnected ? "connected" : "offline"}
        tone={bridgeConnected ? "success" : "danger"}
      />
      <Pill
        label="Kill switch"
        value={killOn ? "ARMED" : "off"}
        tone={killOn ? "danger" : "success"}
      />
      <Pill
        label="Positions"
        value={`${positionsCount} / ${maxPositions || "-"}`}
        tone={positionsCount === 0 ? "neutral" : "success"}
      />
      <Pill
        label="Convergence"
        value={
          matrix == null
            ? "—"
            : matrix.mode === "single"
              ? `single · ${convergedCount}`
              : `${convergedCount} / ${symbolCount}`
        }
        tone={convergedCount > 0 ? "warn" : "neutral"}
      />
    </div>
  );
}
